'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropertyFilters from './PropertyFilters';
import PropertySearchBar from './PropertySearchBar';
import ContactOwnerModal from './ContactOwnerModal';
import PropertyDetailModal from './PropertyDetailModal';
import PropertyCard from './PropertyCard';
import { Property } from '@/lib/mockProperties';
import { listingService, DBListing } from '@/lib/services/dataService';
import { MapPin, X, Home, Building2, Building, BedDouble, Warehouse, TreePine, Bell, Lock, IndianRupee, Loader2, CheckCircle } from 'lucide-react';
import { useRealtimeListings } from '@/hooks/useRealtimeListings';
import { locationNotificationService } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


function dbToProperty(l: DBListing): Property {
  return {
    id: l.id,
    title: l.title,
    // Protected fields stripped — only fetched after unlock via /api/property/contact-details
    houseType: '',
    bhk: '',
    bedrooms: l.bedrooms,
    bathrooms: 0,
    builtUpArea: 0,
    furnishing: '',
    parking: false,
    waterFacility: l.water_facility,
    powerBackup: false,
    lift: l.lift,
    wifi: l.wifi,
    floorNumber: l.floor_number,
    facing: l.facing,
    availableFrom: '',
    monthlyRent: l.monthly_rent,
    securityDeposit: l.security_deposit,
    maintenanceCharges: l.maintenance_charges,
    negotiable: l.negotiable,
    // Sensitive location/contact fields intentionally omitted
    address: '',
    locality: l.locality,
    city: l.city,
    state: l.state,
    pincode: l.pincode,
    landmarks: '',
    googleMapsLink: '',
    photos: l.photos?.length ? l.photos : ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&q=80'],
    verified: l.verified,
    featured: l.featured,
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    listedOn: l.listed_on || l.created_at?.split('T')[0] || '',
    views: l.views,
    inquiries: l.inquiries_count,
    listingNumber: '',
  };
}

const CITIES = ['Mumbai', 'Pune', 'Bangalore', 'Hyderabad', 'Chennai', 'Delhi', 'Noida', 'Gurgaon', 'Ahmedabad', 'Kolkata'];

const CATEGORY_FILTERS = [
  { label: 'All', value: '', icon: Home },
  { label: 'Apartment', value: 'Apartment', icon: Building2 },
  { label: 'Independent House', value: 'Independent House', icon: Home },
  { label: 'Room', value: 'Room', icon: BedDouble },
  { label: 'Villa', value: 'Villa', icon: TreePine },
  { label: 'Commercial', value: 'Commercial', icon: Building },
  { label: 'Studio', value: 'Studio', icon: Warehouse },
];

const FREE_VIEWS_LIMIT = 5;
const UNLOCK_PRICE = 29;

// ── Pay-to-unlock modal ───────────────────────────────────────────────────────
interface PaywallModalProps {
  property: Property;
  onClose: () => void;
  onUnlocked: () => void;
}

function PaywallModal({ property, onClose, onUnlocked }: PaywallModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!user) {
      window.location.href = '/sign-up-login-screen?tab=login';
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Create Razorpay order
      const res = await fetch('/api/razorpay/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: UNLOCK_PRICE,
          currency: 'INR',
          receipt: `unlock_${property.id}_${Date.now()}`,
          notes: {
            listing_id: property.id,
            user_id: user.id,
            listing_title: property.title,
            listing_type: 'view_unlock',
          },
        }),
      });
      const order = await res.json();
      if (!res.ok || !order.id) throw new Error(order.error || 'Failed to create order');

      const keyId = process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID || order.key_id;

      const rzp = new (window as any).Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'tolettu.com',
        description: `Unlock: ${property.title}`,
        order_id: order.id,
        prefill: {
          email: user.email || '',
          name: user.user_metadata?.full_name || '',
        },
        theme: { color: '#7C3AED' },
        handler: async (response: any) => {
          try {
            const verifyRes = await fetch('/api/razorpay/unlock-property', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                listing_id: property.id,
                user_id: user.id,
              }),
            });
            let result = await verifyRes.json();
            if (result.success) {
              onUnlocked();
            } else {
              setError('Payment verification failed. Please contact support.');
            }
          } catch {
            setError('Payment verification failed. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      });
      rzp.open();
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-sm p-6 fade-in">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted transition-colors" aria-label="Close">
          <X size={18} />
        </button>
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <Lock size={26} className="text-primary" />
          </div>
          <h2 className="text-lg font-extrabold text-foreground mb-1">Unlock Property Details</h2>
          <p className="text-sm text-muted-foreground">
            You&apos;ve used your 5 free views. Pay <span className="font-bold text-primary">₹{UNLOCK_PRICE}</span> to view full details of this property.
          </p>
        </div>

        {/* Property preview */}
        <div className="bg-muted/40 rounded-xl p-3 mb-5 text-left">
          <div className="text-sm font-bold text-foreground line-clamp-1">{property.title}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <MapPin size={11} />
            {property.locality}, {property.city}
          </div>
          <div className="text-sm font-extrabold text-primary mt-1">₹{property.monthlyRent.toLocaleString('en-IN')}/month</div>
        </div>

        <div className="space-y-2 mb-5 text-left">
          {['Full owner contact details', 'Complete address & Google Maps link', 'All property photos', 'Amenities & specifications'].map((item) => (
            <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle size={13} className="text-green-500 shrink-0" />
              {item}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 font-medium">{error}</div>
        )}

        <button
          onClick={handlePay}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm transition-colors disabled:opacity-60"
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" />Processing...</>
          ) : (
            <><IndianRupee size={16} />Pay ₹{UNLOCK_PRICE} to Unlock</>
          )}
        </button>
        <p className="text-xs text-muted-foreground text-center mt-3">Secure payment via Razorpay · One-time unlock</p>
      </div>
    </div>
  );
}

export default function PropertyListingsContent() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    bhk: '',
    minRent: '',
    maxRent: '',
    houseType: '',
    furnishing: '',
    availability: '',
  });
  const [sortBy, setSortBy] = useState<'newest' | 'rent-asc' | 'rent-desc' | 'area-desc'>('newest');
  const [contactProperty, setContactProperty] = useState<Property | null>(null);
  const [detailProperty, setDetailProperty] = useState<Property | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detectedCity, setDetectedCity] = useState<string | null>(null);
  const [locationBannerDismissed, setLocationBannerDismissed] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [notifSubscribed, setNotifSubscribed] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [rawDetectedLocation, setRawDetectedLocation] = useState<string | null>(null);
  const [detectedLocationFilter, setDetectedLocationFilter] = useState<string | null>(null);
  const { user } = useAuth();

  // Pay-per-view state
  const [freeViewsUsed, setFreeViewsUsed] = useState(0);
  const [unlockedListings, setUnlockedListings] = useState<Set<string>>(new Set());
  const [paywallProperty, setPaywallProperty] = useState<Property | null>(null);
  const [loadingUnlocked, setLoadingUnlocked] = useState(false);

  // Load free views count from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = parseInt(localStorage.getItem('free_views_used') || '0', 10);
      setFreeViewsUsed(stored);
    }
  }, []);

  // Load unlocked listings from Supabase for logged-in users
  useEffect(() => {
    if (!user) return;
    setLoadingUnlocked(true);
    const supabase = createClient();
    supabase
      .from('unlocked_listings')
      .select('listing_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (data) {
          setUnlockedListings(new Set(data.map((r: any) => r.listing_id)));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingUnlocked(false));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setLoadingProperties(true);
    listingService.getActiveListings()
      .then((listings) => { if (!cancelled) setProperties(listings.map(dbToProperty)); })
      .catch(() => { if (!cancelled) setProperties([]); })
      .finally(() => { if (!cancelled) setLoadingProperties(false); });
    return () => { cancelled = true; };
  }, []);

  useRealtimeListings({
    onInsert: (newListing) => {
      if (newListing.status !== 'Active') return;
      setProperties((prev) => {
        if (prev.some((p) => p.id === newListing.id)) return prev;
        return [dbToProperty(newListing), ...prev];
      });
    },
    onUpdate: (updated) => {
      if (updated.status === 'Active') {
        setProperties((prev) => {
          const exists = prev.some((p) => p.id === updated.id);
          if (exists) return prev.map((p) => (p.id === updated.id ? dbToProperty(updated) : p));
          return [dbToProperty(updated), ...prev];
        });
      } else {
        setProperties((prev) => prev.filter((p) => p.id !== updated.id));
      }
    },
    onDelete: (id) => { setProperties((prev) => prev.filter((p) => p.id !== id)); },
  });

  // Auto-detect city via geolocation
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          const city = data?.address?.city || data?.address?.town || data?.address?.village || data?.address?.suburb || data?.address?.state_district || '';
          const area = data?.address?.suburb || data?.address?.neighbourhood || data?.address?.quarter || '';
          const displayLocation = city || area || '';
          if (displayLocation) {
            setRawDetectedLocation(displayLocation);
          }
          const matched = CITIES.find(c => city.toLowerCase().includes(c.toLowerCase()));
          if (matched) {
            setDetectedCity(matched);
            setFilters(prev => ({ ...prev, city: matched }));
          } else if (displayLocation) {
            setDetectedLocationFilter(displayLocation);
          }
        } catch { /* silent */ }
      },
      () => { /* permission denied */ },
      { timeout: 5000 }
    );
  }, []);

  const handleSubscribeNotifications = async () => {
    if (!user || !detectedCity) return;
    setNotifLoading(true);
    try {
      await locationNotificationService.subscribe({
        user_id: user.id,
        city: detectedCity,
        state: '',
        email: user.email || '',
      });
      setNotifSubscribed(true);
    } catch { /* silent */ } finally {
      setNotifLoading(false);
    }
  };

  const handleCategoryClick = (categoryValue: string) => {
    setActiveCategory(categoryValue);
    if (categoryValue === 'Commercial') {
      setFilters(prev => ({ ...prev, houseType: '' }));
    } else {
      setFilters(prev => ({ ...prev, houseType: categoryValue }));
    }
  };

  const filteredProperties = useMemo(() => {
    let result = [...properties];
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter((p) =>
        p?.title?.toLowerCase()?.includes(q) ||
        p?.city?.toLowerCase()?.includes(q) ||
        p?.locality?.toLowerCase()?.includes(q) ||
        p?.address?.toLowerCase()?.includes(q) ||
        p?.listingNumber?.toLowerCase()?.includes(q)
      );
    }
    if (filters?.city) {
      result = result?.filter((p) => p?.city?.toLowerCase() === filters?.city?.toLowerCase());
    } else if (detectedLocationFilter) {
      const loc = detectedLocationFilter.toLowerCase();
      result = result?.filter((p) =>
        p?.city?.toLowerCase()?.includes(loc) ||
        p?.locality?.toLowerCase()?.includes(loc) ||
        p?.address?.toLowerCase()?.includes(loc) ||
        loc.includes(p?.city?.toLowerCase() || '') ||
        loc.includes(p?.locality?.toLowerCase() || '')
      );
    }
    if (filters?.bhk) result = result?.filter((p) => p?.bhk === filters?.bhk);
    if (activeCategory === 'Commercial') {
      result = result?.filter((p) => ['Commercial Room', 'Commercial Office', 'Office Space', 'Shop/Retail', 'Warehouse', 'Commercial'].some(t => p?.houseType?.toLowerCase().includes(t.toLowerCase())));
    } else if (filters?.houseType) {
      result = result?.filter((p) => p?.houseType === filters?.houseType);
    }
    if (filters?.furnishing) result = result?.filter((p) => p?.furnishing === filters?.furnishing);
    if (filters?.minRent) result = result?.filter((p) => p?.monthlyRent >= parseInt(filters?.minRent));
    if (filters?.maxRent) result = result?.filter((p) => p?.monthlyRent <= parseInt(filters?.maxRent));
    switch (sortBy) {
      case 'rent-asc': result?.sort((a, b) => a?.monthlyRent - b?.monthlyRent); break;
      case 'rent-desc': result?.sort((a, b) => b?.monthlyRent - a?.monthlyRent); break;
      case 'area-desc': result?.sort((a, b) => b?.builtUpArea - a?.builtUpArea); break;
      default: result?.sort((a, b) => new Date(b.listedOn)?.getTime() - new Date(a.listedOn)?.getTime()); break;
    }
    return result;
  }, [properties, searchQuery, filters, sortBy, activeCategory, detectedLocationFilter]);

  const handleResetFilters = () => {
    setFilters({ city: '', bhk: '', minRent: '', maxRent: '', houseType: '', furnishing: '', availability: '' });
    setSearchQuery('');
    setActiveCategory('');
  };

  const activeFilterCount = Object.values(filters)?.filter(Boolean)?.length + (searchQuery ? 1 : 0);

  // Pay-per-view: check if user can view this property
  const canViewProperty = useCallback((property: Property, index: number): boolean => {
    // First 5 listings are always free
    if (index < FREE_VIEWS_LIMIT) return true;
    // If user has unlocked this listing
    if (unlockedListings.has(property.id)) return true;
    return false;
  }, [unlockedListings]);

  const handleViewDetail = (property: Property, index: number) => {
    if (canViewProperty(property, index)) {
      // Track free view usage
      if (index < FREE_VIEWS_LIMIT && typeof window !== 'undefined') {
        const newCount = Math.max(freeViewsUsed, index + 1);
        setFreeViewsUsed(newCount);
        localStorage.setItem('free_views_used', String(newCount));
      }
      setDetailProperty(property);
      listingService.incrementViews(property.id);
    } else {
      setPaywallProperty(property);
    }
  };

  const handleUnlocked = () => {
    if (!paywallProperty) return;
    setUnlockedListings((prev) => new Set([...prev, paywallProperty.id]));
    setDetailProperty(paywallProperty);
    setPaywallProperty(null);
    listingService.incrementViews(paywallProperty.id);
  };

  return (
    <>
      <div id="listings" className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
        {/* Location detected banner */}
        {detectedCity && !locationBannerDismissed && (
          <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-2xl bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <MapPin size={15} className="shrink-0" />
              <span>Showing listings near <strong>{detectedCity}</strong> based on your location.</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {user && !notifSubscribed && (
                <button
                  suppressHydrationWarning
                  onClick={handleSubscribeNotifications}
                  disabled={notifLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  <Bell size={12} />
                  {notifLoading ? 'Subscribing...' : `Notify me for ${detectedCity}`}
                </button>
              )}
              {notifSubscribed && (
                <span className="flex items-center gap-1 text-xs text-blue-700 font-semibold bg-blue-100 px-2 py-1 rounded-lg">
                  <Bell size={11} />Notifications on
                </span>
              )}
              <button suppressHydrationWarning onClick={() => setLocationBannerDismissed(true)} className="text-blue-400 hover:text-blue-600 transition-colors" aria-label="Dismiss"><X size={16} /></button>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {filters.city
              ? `Rental Properties in ${filters.city}`
              : rawDetectedLocation
              ? `Rental Properties in ${rawDetectedLocation}`
              : 'Rental Properties in India'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {loadingProperties ? 'Loading properties...' : `${filteredProperties?.length} verified properties available for rent`}
          </p>
        </div>

        {/* Free views info banner */}
        {filteredProperties.length > FREE_VIEWS_LIMIT && (
          <div className="flex items-center gap-3 mb-5 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200">
            <Lock size={15} className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-700">
              <span className="font-bold">First {FREE_VIEWS_LIMIT} listings are free.</span> View more properties for just <span className="font-bold">₹{UNLOCK_PRICE}/property</span>.
            </p>
          </div>
        )}

        {/* Category Filter Buttons */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 scrollbar-thin">
          {CATEGORY_FILTERS.map(({ label, value, icon: Icon }) => (
            <button
              suppressHydrationWarning
              key={`cat-${label}`}
              onClick={() => handleCategoryClick(value)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-150 border ${
                activeCategory === value
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <PropertySearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          activeFilterCount={activeFilterCount}
          onToggleFilters={() => setFiltersOpen(!filtersOpen)}
          filtersOpen={filtersOpen}
        />

        <div className="flex gap-6 mt-6">
          {/* Mobile Bottom Sheet Overlay */}
          {filtersOpen && (
            <div className="fixed inset-0 z-40 md:hidden" aria-modal="true" role="dialog">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setFiltersOpen(false)} aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl shadow-2xl z-50 flex flex-col" style={{ maxHeight: '85vh' }}>
                <div className="flex justify-center pt-3 pb-1 shrink-0">
                  <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
                </div>
                <div className="overflow-y-auto flex-1 px-4 pb-6 pt-2">
                  <PropertyFilters filters={filters} onChange={setFilters} onReset={handleResetFilters} activeCount={activeFilterCount} onClose={() => setFiltersOpen(false)} />
                </div>
              </div>
            </div>
          )}

          {/* Desktop Sidebar */}
          <aside className="shrink-0 w-72 xl:w-80 hidden md:block">
            <div className="space-y-4">
              <PropertyFilters filters={filters} onChange={setFilters} onReset={handleResetFilters} activeCount={activeFilterCount} onClose={() => setFiltersOpen(false)} />
            </div>
          </aside>

          {/* Property Grid */}
          <div className="flex-1 min-w-0">
            {loadingProperties ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="rounded-2xl bg-muted animate-pulse h-72" />
                ))}
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <MapPin size={28} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">No properties found</h3>
                <p className="text-muted-foreground text-sm max-w-xs">Try adjusting your search or filters to find available rental properties.</p>
                {activeCategory && (
                  <button suppressHydrationWarning onClick={() => handleCategoryClick('')} className="mt-4 btn-secondary text-sm">
                    Clear Category Filter
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredProperties.map((property, idx) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onContact={setContactProperty}
                    onViewDetail={(p) => handleViewDetail(p, idx)}
                    isLocked={!canViewProperty(property, idx)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {contactProperty && <ContactOwnerModal property={contactProperty} onClose={() => setContactProperty(null)} />}
        {detailProperty && (
          <PropertyDetailModal
            property={detailProperty}
            onClose={() => setDetailProperty(null)}
            onContact={() => { setContactProperty(detailProperty); setDetailProperty(null); }}
          />
        )}
        {paywallProperty && (
          <PaywallModal
            property={paywallProperty}
            onClose={() => setPaywallProperty(null)}
            onUnlocked={handleUnlocked}
          />
        )}
      </div>
    </>
  );
}
